<template>
  <xh-select-empty
    v-model="selectedValue"
    :placeholder="'请选择' + modalTitle"
    :treeData
    :disabled
    :treeDataDictionary
    :multiple
    v-bind="$attrs"
    @click="openModal"
    v-if="selectVisible && showSelect"
  />
  <a-form-item-rest>
    <xh-tree-select-modal
      v-model="modalVisible"
      v-model:expandedKeys="expandedKeys"
      v-model:loadedKeys="loadedKeys"
      :selectedKeys="selectedValue"
      :checkedKeys="checkedValue"
      ref="xhTreeSelectModalRef"
      :checkable
      :modalTitle
      :doNotCloseOnKeysNull
      :loadData
      :modalWidth
      :treeData="isAffiliatedOrganization ? treeData : props.treeData"
      :multiple
      :treeDataDictionary
      :searchData
      @confirm="handleConfirm"
      :isAffiliatedOrganization="isAffiliatedOrganization"
    >
      <!-- 通过便利实现插槽透传 -->
      <template v-for="(_, key) in $slots" :key="key" #[key]="slotProps">
        <slot :name="key" v-bind="slotProps"></slot>
      </template>
      <template #filterSearch>
        <div class="flex" v-if="isAffiliatedOrganization">
          <a-input key="name" v-model:value="searchProps.name" placeholder="按名称搜索" class="mr-2 flex-1" />
          <a-input key="code" v-model:value="searchProps.code" placeholder="按编码搜索" class="mr-2 flex-1" />
          <a-button type="primary" @click="handleSearch">搜索</a-button>
        </div>
      </template>
      <template #title="{ title, orgType, code }" v-if="!$slots.title && preset === 'organization'">
        {{ title }}
        <template v-if="orgType !== undefined && orgType !== null">
          <!-- 0-集团；1-公司；2-部门；3-小连锁 -->
          {{ code ? `（${code}）` : '' }}
          <span class="bg-yellow" v-if="orgType === 0">集</span>
          <span class="bg-red" v-else-if="orgType === 1">公</span>
          <span class="bg-blue" v-else-if="orgType === 2">部</span>
          <span class="bg-purple" v-else>小</span>
        </template>
      </template>

      <template #switcherIcon="{ switcherCls }" v-if="!$slots.switcherIcon && preset === 'user'"><down-outlined :class="switcherCls" /></template>
      <template #icon="{ selectable }" v-if="!$slots.icon && preset === 'user'">
        <template v-if="!selectable">
          <span class="i-carbon:load-balancer-global"></span>
        </template>
        <template v-else>
          <!-- <span class="i-carbon:user-avatar" v-if="!selected"></span> -->
          <!-- <span class="i-carbon:user-avatar-filled" v-else></span> -->
        </template>
      </template>
      <template #selectedList="{ selectedItems, checkedItems }" v-if="!$slots.selectedList && preset === 'user'">
        <div
          v-for="item in checkable ? getSelectedList(checkedItems) : selectedItems"
          :key="item.key"
          class="border-bottom-px flex justify-between p-2"
        >
          <div class="flex items-center">
            <!-- <img v-if="item.avatar" :src="item.avatar" alt="" class="mr-2 h-10 w-10 rounded-5" /> -->
            <!-- <span v-else class="i-carbon:user-avatar mr-2 h-10 w-10 rounded-5"></span> -->
            <div class="flex flex-col">
              <span>{{ item.userAccount ? `${item.title}/${item.userAccount}` : item.title }}</span>
              <span class="c-gray-5">{{ item.orgNameTree }} </span>
            </div>
          </div>
          <DeleteOutlined class="cursor-pointer text-xl" @click="deleteOneSelect(item.key as string)" />
        </div>
      </template>
    </xh-tree-select-modal>
  </a-form-item-rest>
</template>
<script lang="ts" setup>
import { DownOutlined, DeleteOutlined } from '@ant-design/icons-vue'
import type { ITreeSelectModalProps } from './types'
import type { IValueType } from '@/components/types'
import type { TreeProps } from 'ant-design-vue'
import type { DataNode } from 'ant-design-vue/es/tree'
import { flattenOrganizationTree } from '@/tools/orgTree'
import { cloneDeep } from 'lodash-es'

const xhTreeSelectModalVisible = ref(false)
const visible = defineModel<boolean>('visible')

const expandedKeys = defineModel<TreeProps['expandedKeys']>('expandedKeys')
const loadedKeys = defineModel<TreeProps['loadedKeys']>('loadedKeys')
const selectedValue = defineModel<IValueType>('value', { required: true })
const checkedValue = defineModel<IValueType>('checkedValue', { required: true })
type ITreeSelectWithModalProps = {
  preset?: 'user' | 'organization'
  selectVisible?: boolean
  showSelect?: boolean
  disabled?: boolean
  checkable?: boolean
} & Omit<ITreeSelectModalProps, 'selectedKeys'>
const props = withDefaults(defineProps<ITreeSelectWithModalProps>(), {
  selectVisible: true,
  showSelect: true,
  disabled: false,
  default: '',
  checkable: false,
})
const treeData = ref<DataNode[]>([])
// 是否是所属组织
const isAffiliatedOrganization = computed(() => {
  return props?.modalTitle?.indexOf('所属组织') !== -1 && props.preset === 'organization'
})
const getSelectedList = (list: DataNode[]) => {
  const result = list?.filter((item) => {
    if (item.parentId && (checkedValue.value as unknown as DataNode[])?.includes(item.parentId)) {
      return false
    } else {
      return true
    }
  })
  return result
}

watch(
  () => props.treeData,
  (val) => {
    if (!val && props.preset !== 'organization') {
      return
    }
    treeData.value = cloneDeep(val)
  },
  {
    immediate: true,
    deep: true,
  }
)

const searchProps = reactive<{
  code?: string
  name?: string
}>({
  name: '',
  code: '',
})
const openModal = () => {
  if (props.disabled) return
  xhTreeSelectModalVisible.value = true
}
const modalVisible = computed({
  get: () => {
    if (props.selectVisible) {
      return xhTreeSelectModalVisible.value
    } else {
      return visible.value!
    }
  },
  set: (val) => {
    if (props.selectVisible) {
      xhTreeSelectModalVisible.value = val
    } else {
      visible.value = val
    }
  },
})
const xhTreeSelectModalRef = ref()
const deleteOneSelect = (key: string) => {
  xhTreeSelectModalRef.value!.deleteOneSelect(key)
}
defineExpose({
  xhTreeSelectModalRef,
  openModal,
})
const emit = defineEmits<{
  confirm: [selectedKeys: IValueType, selectedItems?: IAcceptableType, list?: IAcceptableType[]]
}>()
const handleConfirm = (val: IValueType, items?: IAcceptableType, list?: IAcceptableType[]) => {
  if (props.checkable) {
    checkedValue.value = val
  } else {
    selectedValue.value = val
  }
  emit('confirm', val, items, list)
}
//组织树查询
const handleSearch = () => {
  const result = flattenOrganizationTree(cloneDeep(props.treeData), {
    ...searchProps,
  })
  treeData.value = cloneDeep(result)
}
</script>
<style lang="scss" scoped></style>
